<template>
  <NcrmRecordEditDialog
    v-model="dialog"
    :title="dialogTitle"
    :subtitle="$t('ncrm.contracts.details.dialogs.payment.subtitle')"
    :loading="saving"
    :save-disabled="!canSubmitForm"
    :save-label="saveLabel"
    @save="submitForm"
  >
    <v-row dense>
      <template v-if="isRecordMode">
        <v-col v-if="availableRecordablePaymentItems.length > 1" cols="12">
          <v-select
            v-model="paymentForm.payment_id"
            :items="recordablePaymentOptions"
            item-title="label"
            item-value="id"
            :menu-props="selectMenuProps"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.scheduledPayment')"
            @update:model-value="handleRecordPaymentSelection"
          />
        </v-col>
        <v-col v-if="!availableRecordablePaymentItems.length" cols="12">
          <v-alert type="info" density="comfortable">
            {{ $t("ncrm.contracts.details.dialogs.payment.noRecordablePayments") }}
          </v-alert>
        </v-col>
        <template v-else-if="selectedPaymentFormItem">
          <v-col v-if="availableRecordablePaymentItems.length === 1" cols="12">
            <v-alert type="success" density="comfortable">
              {{
                $t("ncrm.contracts.details.dialogs.payment.autoSelectedSingle", {
                  payment: recordablePaymentLabel(selectedPaymentFormItem),
                })
              }}
            </v-alert>
          </v-col>
          <v-col cols="12">
            <v-alert type="info" density="compact">
              {{
                $t("ncrm.contracts.details.dialogs.payment.summary", {
                  plan: billingPlanLabel(selectedPaymentFormItem.billing_plan_id),
                  due: formatDate(selectedPaymentFormItem.due_at),
                  amount: formatMoney(selectedPaymentFormItem.amount_due, contractCurrencyCode),
                  remaining: formatMoney(selectedPaymentRemainingAmount, contractCurrencyCode),
                })
              }}
            </v-alert>
          </v-col>
          <v-col cols="12">
            <v-alert type="info" density="comfortable">
              {{ $t("ncrm.contracts.details.dialogs.payment.amountOnlyHint") }}
            </v-alert>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              :model-value="formatMoney(selectedPaymentFormItem.amount_due, contractCurrencyCode)"
              readonly
              :label="$t('ncrm.contracts.details.dialogs.payment.fields.amountDue')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              :model-value="formatMoney(selectedPaymentRemainingAmount, contractCurrencyCode)"
              readonly
              :label="$t('ncrm.contracts.details.dialogs.payment.fields.remainingAmount')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="paymentForm.amount_paid"
              type="number"
              step="0.01"
              min="0"
              :label="$t('ncrm.contracts.details.dialogs.payment.fields.amountPaid')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <NcrmDateTimeField v-model="paymentForm.paid_at" :label="$t('ncrm.contracts.details.dialogs.payment.fields.paidAt')" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="paymentForm.payment_method"
              :items="paymentMethodOptions"
              item-title="label"
              item-value="value"
              clearable
              :menu-props="selectMenuProps"
              :label="$t('ncrm.contracts.details.dialogs.payment.fields.paymentMethod')"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="paymentForm.external_reference"
              :label="$t('ncrm.contracts.details.dialogs.payment.fields.externalReference')"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="paymentForm.notes"
              rows="3"
              :label="$t('ncrm.contracts.details.dialogs.payment.fields.notes')"
            />
          </v-col>
        </template>
      </template>
      <template v-else>
        <v-col cols="12">
          <v-select
            v-model="paymentForm.billing_plan_id"
            :items="billingPlanSelectOptions"
            item-title="label"
            item-value="id"
            :menu-props="selectMenuProps"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.billingPlan')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <NcrmDateTimeField v-model="paymentForm.due_at" :label="$t('ncrm.contracts.details.dialogs.payment.fields.dueAt')" />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="paymentForm.amount_due"
            type="number"
            step="0.01"
            min="0"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.amountDue')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="paymentForm.amount_paid"
            type="number"
            step="0.01"
            min="0"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.amountPaid')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <NcrmDateTimeField v-model="paymentForm.paid_at" :label="$t('ncrm.contracts.details.dialogs.payment.fields.paidAt')" />
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="paymentForm.payment_method"
            :items="paymentMethodOptions"
            item-title="label"
            item-value="value"
            clearable
            :menu-props="selectMenuProps"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.paymentMethod')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="paymentForm.external_reference"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.externalReference')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="paymentForm.payment_index"
            type="number"
            min="1"
            step="1"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.paymentIndexOptional')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            :model-value="paymentStatusLabel(paymentStatusPreviewValue)"
            readonly
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.statusPreview')"
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="paymentForm.notes"
            rows="3"
            :label="$t('ncrm.contracts.details.dialogs.payment.fields.notes')"
          />
        </v-col>
      </template>
    </v-row>
  </NcrmRecordEditDialog>
</template>

<script>
import axios from "axios";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import {
  calculatePaymentRemainingAmount,
  currentDateTimeInputValue,
  fromDateTimeInputValue,
  isPaymentCollectionRecordable,
  normalizeMoneyInput,
  normalizeIntegerInput,
  parseNumeric,
  PAYMENT_METHODS,
  toDateTimeInputValue,
  toMoneyInputValue,
} from "./contractDetailsShared.mjs";

export default {
  name: "NcrmContractPaymentDialog",
  components: {
    NcrmDateTimeField,
    NcrmRecordEditDialog,
  },
  props: {
    contractId: {
      type: [String, Number],
      default: null,
    },
    paymentItems: {
      type: Array,
      default: () => [],
    },
    billingPlanItems: {
      type: Array,
      default: () => [],
    },
    contractCurrencyCode: {
      type: String,
      default: "",
    },
    selectMenuProps: {
      type: Object,
      default: () => ({}),
    },
    billingPlanLabel: {
      type: Function,
      default: () => "-",
    },
    formatDate: {
      type: Function,
      default: () => "-",
    },
    formatMoney: {
      type: Function,
      default: (value) => String(value ?? "-"),
    },
  },
  emits: ["saved", "notify"],
  data() {
    return {
      dialog: false,
      mode: "record",
      saving: false,
      editingPaymentId: null,
      recordPaymentScopeBillingPlanId: null,
      paymentForm: this.createEmptyPaymentForm(),
    };
  },
  computed: {
    normalizedContractId() {
      const parsed = Number(this.contractId || 0);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.trunc(parsed);
    },
    billingPlanSelectOptions() {
      return this.billingPlanItems.map((item) => ({
        id: parseNumeric(item?.id, 0),
        label: this.billingPlanLabel(item?.id),
      }));
    },
    recordablePaymentItems() {
      return this.paymentItems.filter((item) => this.isRecordablePayment(item));
    },
    availableRecordablePaymentItems() {
      const billingPlanId = parseNumeric(this.recordPaymentScopeBillingPlanId, 0);
      if (!billingPlanId) return this.recordablePaymentItems;
      return this.recordablePaymentItems.filter((item) => parseNumeric(item?.billing_plan_id, 0) === billingPlanId);
    },
    recordablePaymentOptions() {
      return this.availableRecordablePaymentItems.map((item) => ({
        id: parseNumeric(item?.id, 0),
        label: this.recordablePaymentLabel(item),
      }));
    },
    selectedPaymentFormItem() {
      const paymentId = parseNumeric(this.paymentForm.payment_id, 0) || this.editingPaymentId;
      if (!paymentId) return null;
      return this.paymentItems.find((item) => parseNumeric(item?.id, 0) === paymentId) || null;
    },
    selectedPaymentRemainingAmount() {
      return this.paymentRemainingAmount(this.selectedPaymentFormItem);
    },
    paymentMethodOptions() {
      return PAYMENT_METHODS.map((value) => ({
        value,
        label: this.$t(`ncrm.contracts.details.dialogs.payment.methods.${value}`),
      }));
    },
    isRecordMode() {
      return this.mode === "record";
    },
    paymentStatusPreviewValue() {
      const amountDue = normalizeMoneyInput(this.paymentForm.amount_due);
      const amountPaid = normalizeMoneyInput(this.paymentForm.amount_paid) ?? 0;
      const dueAt = fromDateTimeInputValue(this.paymentForm.due_at);
      if (!dueAt || amountDue === null) {
        return String(this.paymentForm.status || "").trim().toLowerCase() || "pending";
      }
      if (amountDue > 0 && amountPaid >= amountDue) return "paid";
      if (amountPaid > 0) return "partial";
      const dueDate = new Date(String(dueAt).replace(" ", "T"));
      if (!Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now()) {
        return "overdue";
      }
      return "pending";
    },
    dialogTitle() {
      if (!this.isRecordMode && this.editingPaymentId) return this.$t("ncrm.contracts.details.dialogs.payment.editTitle");
      return this.$t("ncrm.contracts.details.dialogs.payment.createTitle");
    },
    saveLabel() {
      if (!this.isRecordMode && this.editingPaymentId) return this.$t("app.actions.save");
      return this.$t("ncrm.contracts.actions.addPayment");
    },
    canSubmitForm() {
      if (this.saving) return false;
      if (this.isRecordMode) {
        const paymentId = parseNumeric(this.paymentForm.payment_id, 0);
        const amountPaid = normalizeMoneyInput(this.paymentForm.amount_paid);
        return paymentId > 0 && amountPaid !== null && amountPaid > 0;
      }

      const billingPlanId = parseNumeric(this.paymentForm.billing_plan_id, 0);
      const dueAt = String(this.paymentForm.due_at || "").trim();
      const amountDue = normalizeMoneyInput(this.paymentForm.amount_due);
      return billingPlanId > 0 && Boolean(dueAt) && amountDue !== null && amountDue >= 0;
    },
  },
  methods: {
    createEmptyPaymentForm() {
      return {
        payment_id: null,
        billing_plan_id: null,
        payment_index: "",
        due_at: "",
        paid_at: "",
        amount_due: "",
        amount_paid: "",
        status: "",
        payment_method: "",
        external_reference: "",
        notes: "",
      };
    },
    findPaymentById(paymentId) {
      const normalizedId = parseNumeric(paymentId, 0);
      if (!normalizedId) return null;
      return this.paymentItems.find((item) => parseNumeric(item?.id, 0) === normalizedId) || null;
    },
    paymentRemainingAmount(payment = null) {
      return calculatePaymentRemainingAmount(payment);
    },
    isRecordablePayment(payment = null) {
      return isPaymentCollectionRecordable(payment);
    },
    recordablePaymentLabel(payment = null) {
      if (!payment) return "-";
      return [
        this.billingPlanLabel(payment?.billing_plan_id),
        this.formatDate(payment?.due_at),
        this.formatMoney(this.paymentRemainingAmount(payment), this.contractCurrencyCode),
      ].join(" · ");
    },
    paymentStatusLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.contracts.paymentStatuses.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    prefillRecordPaymentFields(payment = null) {
      if (!payment) return;
      const remainingAmount = this.paymentRemainingAmount(payment);
      this.paymentForm.billing_plan_id = parseNumeric(payment?.billing_plan_id, 0) || null;
      this.paymentForm.payment_index = payment?.payment_index ?? "";
      this.paymentForm.due_at = toDateTimeInputValue(payment?.due_at);
      this.paymentForm.amount_due = payment?.amount_due ?? "";
      this.paymentForm.status = String(payment?.status || "").trim().toLowerCase();
      this.paymentForm.amount_paid = remainingAmount > 0 ? toMoneyInputValue(remainingAmount) : "";
      this.paymentForm.paid_at = currentDateTimeInputValue();
      this.paymentForm.payment_method = "";
      this.paymentForm.external_reference = "";
      this.paymentForm.notes = "";
    },
    handleRecordPaymentSelection(value) {
      const payment = this.findPaymentById(value);
      this.paymentForm.payment_id = parseNumeric(value, 0) || null;
      if (!payment) return;
      this.prefillRecordPaymentFields(payment);
    },
    openRecordDialog(payment = null) {
      const selectedPayment = payment && this.isRecordablePayment(payment) ? payment : null;
      const scopedBillingPlanId = parseNumeric(selectedPayment?.billing_plan_id ?? payment?.billing_plan_id ?? payment?.id, 0) || null;
      const availableRecordablePayments = scopedBillingPlanId
        ? this.recordablePaymentItems.filter((item) => parseNumeric(item?.billing_plan_id, 0) === scopedBillingPlanId)
        : this.recordablePaymentItems;

      if (!availableRecordablePayments.length) {
        this.openCreateDialog(payment);
        return;
      }

      this.editingPaymentId = null;
      this.recordPaymentScopeBillingPlanId = scopedBillingPlanId || null;
      this.mode = "record";
      this.paymentForm = this.createEmptyPaymentForm();

      const initialPayment = selectedPayment || (availableRecordablePayments.length === 1 ? availableRecordablePayments[0] : null);
      if (initialPayment) {
        this.paymentForm.payment_id = parseNumeric(initialPayment?.id, 0) || null;
        this.prefillRecordPaymentFields(initialPayment);
      }

      this.dialog = true;
    },
    openCreateDialog(payment = null) {
      this.mode = "create";
      this.editingPaymentId = null;
      this.recordPaymentScopeBillingPlanId = null;
      this.paymentForm = this.createEmptyPaymentForm();
      const billingPlanId = parseNumeric(payment?.billing_plan_id ?? payment?.id, 0)
        || (this.billingPlanItems.length === 1 ? parseNumeric(this.billingPlanItems[0]?.id, 0) : null);
      this.paymentForm.billing_plan_id = billingPlanId || null;
      this.dialog = true;
    },
    openEditDialog(payment) {
      this.recordPaymentScopeBillingPlanId = null;
      this.mode = "edit";
      this.editingPaymentId = parseNumeric(payment?.id, 0) || null;
      this.paymentForm = {
        payment_id: this.editingPaymentId,
        billing_plan_id: parseNumeric(payment?.billing_plan_id, 0) || null,
        payment_index: payment?.payment_index ?? "",
        due_at: toDateTimeInputValue(payment?.due_at),
        paid_at: toDateTimeInputValue(payment?.paid_at),
        amount_due: payment?.amount_due ?? "",
        amount_paid: payment?.amount_paid ?? "",
        status: String(payment?.status || "").toLowerCase(),
        payment_method: payment?.payment_method || "",
        external_reference: payment?.external_reference || "",
        notes: payment?.notes || "",
      };
      this.dialog = true;
    },
    buildPaymentPayload() {
      if (this.isRecordMode) {
        const selectedPayment = this.selectedPaymentFormItem;
        const paymentId = parseNumeric(this.paymentForm.payment_id, 0);
        const collectedNow = normalizeMoneyInput(this.paymentForm.amount_paid);
        if (!paymentId || !selectedPayment || collectedNow === null || collectedNow <= 0) {
          return null;
        }
        const existingAmountPaid = normalizeMoneyInput(selectedPayment?.amount_paid) ?? 0;

        return {
          payment_id: paymentId,
          billing_plan_id: parseNumeric(selectedPayment?.billing_plan_id, 0) || null,
          contract_item_id: parseNumeric(selectedPayment?.contract_item_id, 0) || null,
          payment_index: parseNumeric(selectedPayment?.payment_index, 0) || null,
          due_at: fromDateTimeInputValue(toDateTimeInputValue(selectedPayment?.due_at)),
          amount_due: normalizeMoneyInput(selectedPayment?.amount_due),
          paid_at: fromDateTimeInputValue(this.paymentForm.paid_at) || fromDateTimeInputValue(currentDateTimeInputValue()),
          amount_paid: Math.round((existingAmountPaid + collectedNow) * 100) / 100,
          payment_method: String(this.paymentForm.payment_method || "").trim() || null,
          external_reference: String(this.paymentForm.external_reference || "").trim() || null,
          notes: String(this.paymentForm.notes || "").trim() || null,
        };
      }

      const billingPlanId = parseNumeric(this.paymentForm.billing_plan_id, 0) || null;
      const selectedBillingPlan = this.billingPlanItems.find((item) => parseNumeric(item?.id, 0) === billingPlanId);
      const amountDue = normalizeMoneyInput(this.paymentForm.amount_due);
      const dueAt = fromDateTimeInputValue(this.paymentForm.due_at);
      if (!billingPlanId || !dueAt || amountDue === null || amountDue < 0) {
        return null;
      }

      return {
        billing_plan_id: billingPlanId,
        contract_item_id: parseNumeric(selectedBillingPlan?.contract_item_id, 0) || null,
        payment_index: normalizeIntegerInput(this.paymentForm.payment_index),
        due_at: dueAt,
        paid_at: fromDateTimeInputValue(this.paymentForm.paid_at),
        amount_due: amountDue,
        amount_paid: normalizeMoneyInput(this.paymentForm.amount_paid),
        payment_method: String(this.paymentForm.payment_method || "").trim() || null,
        external_reference: String(this.paymentForm.external_reference || "").trim() || null,
        notes: String(this.paymentForm.notes || "").trim() || null,
      };
    },
    async submitForm() {
      if (!this.normalizedContractId || !this.canSubmitForm) return;
      const payload = this.buildPaymentPayload();
      if (!payload) return;

      this.saving = true;
      try {
        if (this.isRecordMode) {
          await axios.patch(`/ncrm/contracts/${this.normalizedContractId}/payments/${payload.payment_id}`, payload);
          this.$emit("notify", { message: this.$t("ncrm.contracts.messages.paymentCreated"), color: "success" });
        } else if (this.editingPaymentId) {
          await axios.patch(`/ncrm/contracts/${this.normalizedContractId}/payments/${this.editingPaymentId}`, payload);
          this.$emit("notify", { message: this.$t("ncrm.contracts.messages.paymentUpdated"), color: "success" });
        } else {
          await axios.post(`/ncrm/contracts/${this.normalizedContractId}/payments`, payload);
          this.$emit("notify", { message: this.$t("ncrm.contracts.messages.paymentCreated"), color: "success" });
        }
        this.dialog = false;
        this.$emit("saved");
      } catch (error) {
        console.error("[ncrm-contract-payment-dialog] save payment failed", error);
        this.$emit("notify", {
          message: error?.response?.data?.error || this.$t("ncrm.contracts.messages.paymentSaveError"),
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
